'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { MobilePanelId } from '@/features/lobby/panels/MobilePanelChipRail';

/**
 * Lightweight context that lifts the mobile panel chip-rail rendering
 * out of `LobbyClient` / `ChatPage` and into the shared `AppShell`
 * top chrome. Without this, the chip rail can only live inside the
 * route's render tree — which forced it to be a block-flow element
 * inside a measured wrapper (see the historic `mobileTopChromeRef` +
 * `ResizeObserver` in `LobbyClient`) so the canvas viewport could
 * compute around it. Hoisting the rail to a fixed-position slab in the
 * shell lets the middle area become a strict
 * `100dvh - <fixed-top> - <fixed-bottom>` band with no document
 * scroll, matching the design brief.
 *
 * Consumers register their chip-rail config (active panel + change
 * handler + optional unread badges) via `useChipRail(...)`. The shell
 * reads the registered state and renders one rail; when no consumer is
 * registered the rail is hidden (e.g. on `/create`, lobby mode, or
 * routes outside the mobile-chrome match list).
 */

export type ChipRailBadges = Partial<Record<MobilePanelId, 'dot' | null>>;

interface MobileChromeContextValue {
  /** Currently-active panel id, or `null` when no consumer has
   *  registered a chip rail (rail is hidden). */
  active: MobilePanelId | null;
  /** Per-chip unread badges (e.g. chat dot). */
  badges: ChipRailBadges;
  /** Fired by the rail when the user taps a chip. Stable identity —
   *  internally dispatches to whatever the latest registered handler
   *  is via a ref so consumers don't need to memo their callbacks. */
  triggerChange: (panel: MobilePanelId) => void;
  /** Internal: consumers shouldn't call directly; use `useChipRail`. */
  _registerActive: (active: MobilePanelId | null) => void;
  /** Internal: consumers shouldn't call directly; use `useChipRail`. */
  _registerBadges: (badges: ChipRailBadges) => void;
  /** Internal: consumers shouldn't call directly; use `useChipRail`. */
  _registerHandler: (
    handler: ((panel: MobilePanelId) => void) | null,
  ) => void;
}

const MobileChromeContext = createContext<MobileChromeContextValue | null>(null);

export function MobileChromeProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<MobilePanelId | null>(null);
  const [badges, setBadges] = useState<ChipRailBadges>({});
  const handlerRef = useRef<((panel: MobilePanelId) => void) | null>(null);

  const triggerChange = useCallback((panel: MobilePanelId) => {
    handlerRef.current?.(panel);
  }, []);

  const _registerActive = useCallback((next: MobilePanelId | null) => {
    setActive((prev) => (prev === next ? prev : next));
  }, []);

  const _registerBadges = useCallback((next: ChipRailBadges) => {
    setBadges((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length !== nextKeys.length) return next;
      for (const k of nextKeys) {
        if (prev[k as MobilePanelId] !== next[k as MobilePanelId]) return next;
      }
      return prev;
    });
  }, []);

  const _registerHandler = useCallback(
    (handler: ((panel: MobilePanelId) => void) | null) => {
      handlerRef.current = handler;
    },
    [],
  );

  const value = useMemo<MobileChromeContextValue>(
    () => ({
      active,
      badges,
      triggerChange,
      _registerActive,
      _registerBadges,
      _registerHandler,
    }),
    [active, badges, triggerChange, _registerActive, _registerBadges, _registerHandler],
  );

  return (
    <MobileChromeContext.Provider value={value}>
      {children}
    </MobileChromeContext.Provider>
  );
}

/** Read-only access for the shell's chip-rail host (don't call from
 *  consumers — they should use {@link useChipRail} instead). */
export function useMobileChrome(): MobileChromeContextValue | null {
  return useContext(MobileChromeContext);
}

/**
 * Register the chip-rail config for the current route. Pass `active`
 * = `null` to hide the rail (e.g. when the lobby is in `?mode=lobby`).
 *
 * The `onChange` handler is stored in a ref so the registered identity
 * stays stable — callers can pass an inline arrow function without
 * triggering re-renders of the rail itself. `badges` is shallow-
 * compared so steady identity isn't required there either.
 */
export function useChipRail({
  active,
  onChange,
  badges,
}: {
  active: MobilePanelId | null;
  onChange: (panel: MobilePanelId) => void;
  badges?: ChipRailBadges;
}) {
  const ctx = useContext(MobileChromeContext);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (!ctx) return;
    ctx._registerHandler((panel) => onChangeRef.current(panel));
    return () => ctx._registerHandler(null);
  }, [ctx]);

  useEffect(() => {
    if (!ctx) return;
    ctx._registerActive(active);
    return () => ctx._registerActive(null);
  }, [ctx, active]);

  // Memoise badges by stable key to avoid new-object-every-render
  // re-renders. Keys are a closed set of `MobilePanelId`s so a stable
  // sorted JSON form is a robust fingerprint.
  const badgesKey = badges
    ? Object.keys(badges)
        .sort()
        .map((k) => `${k}:${badges[k as MobilePanelId] ?? 'null'}`)
        .join(',')
    : '';
  useEffect(() => {
    if (!ctx) return;
    ctx._registerBadges(badges ?? {});
    return () => ctx._registerBadges({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- badgesKey is the fingerprint
  }, [ctx, badgesKey]);
}
